import { useState, useEffect } from "react";
import {
  ExpressionEditor,
  ExpressionEditorType,
  AutocompleteField,
} from "./ExpressionEditor";
import { SyntaxHighlightedCode } from "../../patterns/SyntaxHighlightedCode";
import { Pencil, X, RotateCcw } from "lucide-react";
import { cn } from "../../primitives/utils";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";

export interface ExpressionField {
  key: string;
  label: string;
  value: string;
  placeholder?: string;
}

interface MultiExpressionEditorProps {
  type: ExpressionEditorType;
  fields: ExpressionField[];
  onChange: (key: string, value: string) => void;
  selectedDatasets?: string[];
  availableDatasets?: Record<string, AutocompleteField[]>;
  availableSignals?: AutocompleteField[];
  /** Optional: Track original values to show unsaved changes */
  originalFields?: ExpressionField[];
  /** Optional: Callback to revert all fields to original values */
  onRevertAll?: () => void;
}

/**
 * MultiExpressionEditor - Columnar multi-expression editor
 *
 * Displays multiple expression fields in a table-like layout:
 * - Left column: field labels
 * - Right column: expressions (collapsed with ellipsis by default)
 * - Click to expand individual expressions for editing
 */
export function MultiExpressionEditor({
  type,
  fields,
  onChange,
  selectedDatasets = [],
  availableDatasets = {},
  availableSignals = [],
  originalFields = [],
  onRevertAll,
}: MultiExpressionEditorProps) {
  const capabilities = usePlatformCapabilities();
  const [expandedField, setExpandedField] = useState<string | null>(null);

  // Handle ESC key to collapse
  useEffect(() => {
    return capabilities.registerGlobalKeydownListener(({ key }) => {
      if (key === "Escape" && expandedField !== null) {
        setExpandedField(null);
      }
    });
  }, [capabilities, expandedField]);

  const toggleField = (key: string) => {
    setExpandedField(expandedField === key ? null : key);
  };

  const getOriginalValue = (key: string): string | undefined => {
    return originalFields.find((f) => f.key === key)?.value;
  };

  const isModified = (field: ExpressionField): boolean => {
    const original = getOriginalValue(field.key);
    return original !== undefined && original !== field.value;
  };

  const revertField = (key: string) => {
    const original = getOriginalValue(key);
    if (original !== undefined) {
      onChange(key, original);
    }
  };

  const hasAnyModifications = (): boolean => {
    return fields.some((field) => isModified(field));
  };

  const truncateExpression = (expr: string, maxLength: number = 60): string => {
    const singleLine = expr.replace(/\s+/g, " ").trim();
    if (singleLine.length <= maxLength) return singleLine;
    return singleLine.slice(0, maxLength) + "...";
  };

  return (
    <div className="border rounded-md overflow-hidden">
      {fields.map((field, index) => {
        const isExpanded = expandedField === field.key;
        const isLast = index === fields.length - 1;
        const modified = isModified(field);

        return (
          <div
            key={field.key}
            className={cn(
              "grid grid-cols-[180px_1fr]",
              !isLast && "border-b",
              modified && "border-l-4 border-l-amber-500",
            )}
          >
            {/* Left column: Field label */}
            <div className="bg-muted/30 p-3 flex items-center justify-between border-r gap-2">
              <span className="text-sm font-medium">{field.label}</span>
              {modified && (
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                  <span className="size-1.5 rounded-full bg-amber-500" />
                  Modified
                </span>
              )}
            </div>

            {/* Right column: Expression */}
            <div className="bg-background">
              {isExpanded ? (
                // Expanded: Full editor
                <div className="p-3 space-y-2">
                  <ExpressionEditor
                    value={field.value}
                    onChange={(value) => onChange(field.key, value)}
                    type={type}
                    selectedDatasets={selectedDatasets}
                    availableDatasets={availableDatasets}
                    availableSignals={availableSignals}
                    placeholder={field.placeholder}
                    minHeight="120px"
                  />
                  <div className="flex justify-between items-center">
                    {modified ? (
                      <button
                        type="button"
                        onClick={() => revertField(field.key)}
                        className="text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors flex items-center gap-1.5"
                      >
                        <RotateCcw className="size-3" />
                        <span>Revert to original</span>
                      </button>
                    ) : (
                      <div />
                    )}
                    <button
                      type="button"
                      onClick={() => setExpandedField(null)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                    >
                      <span className="opacity-60">Press ESC or</span>
                      <span>Close</span>
                      <X className="size-3" />
                    </button>
                  </div>
                </div>
              ) : (
                // Collapsed: Truncated text with click to expand
                <button
                  type="button"
                  onClick={() => toggleField(field.key)}
                  className="w-full p-3 text-left hover:bg-muted/30 transition-colors group"
                  style={{ fontSize: "13px", lineHeight: "1.6" }}
                >
                  <div className="flex items-center justify-between gap-2">
                    {field.value ? (
                      <SyntaxHighlightedCode
                        code={truncateExpression(field.value)}
                        className="font-mono text-foreground/80 group-hover:text-foreground transition-colors flex-1 min-w-0"
                      />
                    ) : (
                      <code className="font-mono text-muted-foreground italic flex-1 min-w-0">
                        {field.placeholder || "Click to edit..."}
                      </code>
                    )}
                    <Pencil className="size-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                  </div>
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Revert All button - only show when there are modifications */}
      {hasAnyModifications() && onRevertAll && (
        <div className="border-t bg-muted/20 p-3 flex justify-end">
          <button
            type="button"
            onClick={() => {
              onRevertAll();
              setExpandedField(null); // Close any expanded editors
            }}
            className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors flex items-center gap-2 font-medium"
          >
            <RotateCcw className="size-4" />
            <span>Revert All Changes</span>
          </button>
        </div>
      )}
    </div>
  );
}
